import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { Modal, Spin, Tabs } from 'antd';
import Countdown from 'antd/lib/statistic/Countdown';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LoggedScreen from 'components/BusinessLogic/ScreenTemplates/LoggedScreen';
import ModalInformationExpiredItems from 'components/Modals/InformationExpiredItems';
import ModalOrderSummary from 'components/Modals/OrderSummary';
import ModalSuccessfulPurchase from 'components/Modals/SuccessfulPurchase';
import OrderSummaryCard from 'components/UI/OrderSummaryCard';
import ProductCartCard from 'components/UI/ProductCartCard';
import { CartTabType } from 'enums/cartTabType';
import { useAppNavigation } from 'hooks/useAppNavigation';
import { isNil } from 'lodash';
import CartItem from 'model/CartItem';
import GfpDetails from 'model/GfpDetails';
import * as S from 'screens/ShoppingCartNew/styles';
import { setTypeOfferSelectedMyOrders } from 'services/config';
import {
  useGetCartItemsQuery,
  useGetDeleteCartMutation,
  useGetDeleteItemCartMutation,
} from 'services/ecommerceApi';
import { cartSelectors, sessionSelectors } from 'store/state/selectors';
import { cartActions } from 'store/state/slices';
import { dateUtils } from 'utils/date';
import { sumMoney } from 'utils/number';
import { calculateCartItemTotal, calculateTotalShipping } from 'utils/util';

function TabEmpty({ message = 'Nenhum item encontrado', subMessage }) {
  return (
    <S.EmptyStateContainer>
      <S.EmptyStateText>{message}</S.EmptyStateText>
      {subMessage && <S.EmptyStateSubText>{subMessage}</S.EmptyStateSubText>}
    </S.EmptyStateContainer>
  );
}

function TabLoading() {
  return (
    <S.EmptyStateContainer>
      <Spin size="large" style={{ color: '#f78e1e' }} />
    </S.EmptyStateContainer>
  );
}
const { TabPane } = Tabs;

const safeGet = function <T>(value: T | null | undefined, fallback: T): T {
  return value ?? fallback;
};

const OFFER_TYPE_TO_NUMBER: Record<string, number> = {
  V: 0,
  R: 1,
  T: 2,
  D: 3,
  onSite: 0,
  received: 1,
  inTransit: 2,
  directed: 3,
};

const parseOfferType = (offerType?: string): number | undefined => {
  if (offerType == null) return undefined;
  return OFFER_TYPE_TO_NUMBER[offerType];
};

const formatPackaging = (item: CartItem): string => {
  const packingName = safeGet(item.packingName, 'Embalagem');
  return `${packingName}`;
};

export default function ShoppingCartNew() {
  const { navigateToProducts } = useAppNavigation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<CartTabType>(CartTabType.ENP);
  const initialTabResolvedRef = useRef(false);
  const lastKnownVolOfferIdRef = useRef<number>(0);
  const lastKnownLkpOfferIdRef = useRef<number>(0);
  const refetchVolRef = useRef<(() => void) | undefined>(undefined);
  const refetchLkpRef = useRef<(() => void) | undefined>(undefined);
  const volTimerExpiredRef = useRef(false);
  const lkpTimerExpiredRef = useRef(false);

  const volOfferId = useSelector(cartSelectors.getVolOfferId);
  const lkpOfferId = useSelector(cartSelectors.getLkpOfferId);
  const lkpCartExpiration = useSelector(cartSelectors.getLkpExpiration);
  const volCartExpiration = useSelector(cartSelectors.getVolExpiration);
  const billingDate = useSelector(sessionSelectors.getSelectedBillingDate);
  const deliveryDate = useSelector(sessionSelectors.getSelectedDeliveryDate);
  const producerUrl = useSelector(sessionSelectors.getProducerUrl);
  const refetchCartItems = useSelector(cartSelectors.getRefetchCartItems);

  const [cartItemsVol, setCartItemsVol] = useState<CartItem[]>([]);
  const [cartItemsLkp, setCartItemsLkp] = useState<CartItem[]>([]);
  const [
    cartItemsVolGroupedByComposition,
    setCartItemsVolGroupedByComposition,
  ] = useState<Array<CartItem[] | CartItem>>([]);
  const [cartItemsNoGfp, setCartItemsNoGfp] = useState<CartItem[]>([]);
  const [cartItemsWithGfp, setCartItemsWithGfp] = useState<CartItem[]>([]);
  const [lkpExpiration, setLkpExpiration] = useState<{
    isExpired: boolean;
    expirationDate: number;
  }>({ isExpired: false, expirationDate: 0 });

  const [volExpiration, setVolExpiration] = useState<{
    isExpired: boolean;
    expirationDate: number;
  }>({ isExpired: false, expirationDate: 0 });

  const [modalInformationsItensExpired, setModalInformationsItensExpired] =
    useState<boolean>(false);
  const [modalOrderSummaryIsVisible, setModalOrderSummaryIsVisible] =
    useState<boolean>(false);
  const [showModalSuccess, setShowModalSuccess] = useState<boolean>(false);

  const [getDeleteItemCart] = useGetDeleteItemCartMutation();
  const [getDeleteCart] = useGetDeleteCartMutation();

  const {
    data: volCartItems,
    refetch: refetchCartItemsVol,
    isFetching: isFetchingENP,
  } = useGetCartItemsQuery({ cartId: volOfferId }, { skip: !volOfferId });

  const {
    data: lkpCartItems,
    refetch: refetchCartItemsLkp,
    isFetching: isFetchingLKP,
  } = useGetCartItemsQuery({ cartId: lkpOfferId }, { skip: !lkpOfferId });

  useEffect(() => {
    if (initialTabResolvedRef.current) return;
    if ((volOfferId && isFetchingENP) || (lkpOfferId && isFetchingLKP)) return;

    const hasEnpItems = volCartItems && volCartItems.length > 0;
    const hasLkpItems = lkpCartItems && lkpCartItems.length > 0;

    if (hasEnpItems && hasLkpItems) {
      const volExp = volCartExpiration
        ? new Date(volCartExpiration).getTime()
        : Infinity;
      const lkpExp = lkpCartExpiration
        ? new Date(lkpCartExpiration).getTime()
        : Infinity;
      const validVolExp = !Number.isNaN(volExp) ? volExp : Infinity;
      const validLkpExp = !Number.isNaN(lkpExp) ? lkpExp : Infinity;

      if (validLkpExp < validVolExp) {
        setActiveTab(CartTabType.LKP);
      }
    } else if (hasLkpItems && !hasEnpItems) {
      setActiveTab(CartTabType.LKP);
    }

    initialTabResolvedRef.current = true;
  }, [
    volCartItems,
    lkpCartItems,
    volCartExpiration,
    lkpCartExpiration,
    isFetchingENP,
    isFetchingLKP,
    volOfferId,
    lkpOfferId,
  ]);

  useEffect(() => {
    if (volOfferId) {
      lastKnownVolOfferIdRef.current = volOfferId;
      refetchVolRef.current = refetchCartItemsVol;
    } else {
      setCartItemsVol([]);
    }
    if (lkpOfferId) {
      lastKnownLkpOfferIdRef.current = lkpOfferId;
      refetchLkpRef.current = refetchCartItemsLkp;
    } else {
      setCartItemsLkp([]);
    }
  }, [volOfferId, lkpOfferId, refetchCartItemsVol, refetchCartItemsLkp]);

  useEffect(() => {
    if (refetchCartItems) {
      if (lastKnownLkpOfferIdRef.current) {
        refetchLkpRef.current?.();
      }
      if (lastKnownVolOfferIdRef.current) {
        refetchVolRef.current?.();
      }
      dispatch(cartActions.setRefetchRequestCartItems(false));
    }
  }, [dispatch, refetchCartItems]);

  useEffect(() => {
    const arrayItemsVol: Array<CartItem | CartItem[]> = [];
    const compositionKeysAdded: string[] = [];

    cartItemsVol.forEach((item) => {
      if (!item.compositionKey) {
        arrayItemsVol.push(item);
      } else if (!compositionKeysAdded.includes(item.compositionKey)) {
        arrayItemsVol.push(
          cartItemsVol.filter(
            (cartItem) => cartItem.compositionKey === item.compositionKey,
          ),
        );
        compositionKeysAdded.push(item.compositionKey);
      }
    });

    setCartItemsVolGroupedByComposition(arrayItemsVol);
  }, [cartItemsVol]);

  useEffect(() => {
    // VOL
    if (volTimerExpiredRef.current) {
      if (!volCartItems || volCartItems.length === 0) {
        volTimerExpiredRef.current = false;
        setCartItemsVol([]);
      }
    } else if (volCartItems && volCartItems.length > 0) {
      const arrayItemsVol = [...volCartItems].map((item) => ({
        ...item,
        isExpired: dateUtils.isExpired(
          dateUtils.AddTimeZoneInExpirationDate(item.expirationDate),
        ),
      }));
      setCartItemsVol(arrayItemsVol);
    } else {
      setCartItemsVol([]);
    }

    // LKP
    if (lkpTimerExpiredRef.current) {
      if (!lkpCartItems || lkpCartItems.length === 0) {
        lkpTimerExpiredRef.current = false;
        setCartItemsLkp([]);
        setCartItemsNoGfp([]);
        setCartItemsWithGfp([]);
      }
    } else if (lkpCartItems && lkpCartItems.length > 0) {
      const arrayItemsLkp = [...lkpCartItems].map((item) => ({
        ...item,
        isExpired: dateUtils.isExpired(
          dateUtils.AddTimeZoneInExpirationDate(item.expirationDate),
        ),
      }));
      setCartItemsLkp(arrayItemsLkp);

      const noGfp = arrayItemsLkp.filter((item) => !item.gfpLineId);
      const withGfp = arrayItemsLkp.filter((item) => item.gfpLineId);

      setCartItemsNoGfp(noGfp.length ? noGfp : []);
      setCartItemsWithGfp(withGfp.length ? withGfp : []);
    } else {
      setCartItemsLkp([]);
      setCartItemsNoGfp([]);
      setCartItemsWithGfp([]);
    }
  }, [volCartItems, lkpCartItems]);

  useEffect(() => {
    if (cartItemsVol.length > 0 && volCartExpiration) {
      const timestamp = new Date(volCartExpiration).getTime();

      setVolExpiration({
        isExpired: !isNaN(timestamp) && timestamp <= Date.now(),
        expirationDate: isNaN(timestamp) ? 0 : timestamp,
      });
    }
  }, [cartItemsVol, volCartExpiration]);

  useEffect(() => {
    if (cartItemsLkp.length > 0 && lkpCartExpiration) {
      const timestamp = new Date(lkpCartExpiration).getTime();

      setLkpExpiration({
        isExpired: !isNaN(timestamp) && timestamp <= Date.now(),
        expirationDate: isNaN(timestamp) ? 0 : timestamp,
      });
    }
  }, [cartItemsLkp, lkpCartExpiration]);

  const updateDerivedStatesAfterRemoval = useCallback(
    (removedItemId: number) => {
      setCartItemsLkp((prevItems) => {
        const filteredItems = prevItems.filter(
          (item) => item.id !== removedItemId,
        );

        const noGfp = filteredItems.filter((item) => !item.gfpLineId);
        const withGfp = filteredItems.filter((item) => item.gfpLineId);

        setCartItemsNoGfp(noGfp.length ? noGfp : []);
        setCartItemsWithGfp(withGfp.length ? withGfp : []);

        return filteredItems;
      });

      setCartItemsVol((prevItems) =>
        prevItems.filter((item) => item.id !== removedItemId),
      );
    },
    [],
  );

  const onClickRemoveItem = useCallback(
    (id: number) => {
      Modal.confirm({
        title: 'Atenção',
        icon: <ExclamationCircleOutlined />,
        content: 'Tem certeza que deseja remover o item?',
        okText: 'Sim, remover',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk: async () => {
          try {
            await getDeleteItemCart({ id }).unwrap();

            updateDerivedStatesAfterRemoval(id);

            console.log('Item removido com sucesso');
          } catch (error) {
            console.error('Erro ao remover item do carrinho:', error);
          } finally {
            Modal.destroyAll();

            await Promise.all([refetchCartItemsVol(), refetchCartItemsLkp()]);

            const volData = volCartItems || [];
            const lkpData = lkpCartItems || [];
            const itemStillExists = [...volData, ...lkpData].some(
              (item) => item.id === id,
            );

            if (itemStillExists) {
              console.warn(
                'Item ainda existe no servidor após exclusão, forçando novo refetch',
              );
              await Promise.all([refetchCartItemsVol(), refetchCartItemsLkp()]);
            }
          }
        },
      });
    },
    [
      getDeleteItemCart,
      refetchCartItemsVol,
      refetchCartItemsLkp,
      updateDerivedStatesAfterRemoval,
      volCartItems,
      lkpCartItems,
    ],
  );

  const enpTotals = useMemo(() => {
    if (cartItemsVol.length === 0) {
      return { itemsCount: 0, shippingCost: 0, total: 0 };
    }

    const shippingCost = cartItemsVol.reduce((acc, item) => acc + calculateTotalShipping(item), 0);

    return {
      itemsCount: cartItemsVolGroupedByComposition.length,
      shippingCost,
      total: sumMoney(cartItemsVol.map(calculateCartItemTotal)),
    };
  }, [cartItemsVol, cartItemsVolGroupedByComposition.length]);

  const lkpTotals = useMemo(() => {
    if (cartItemsLkp.length === 0) {
      return { itemsCount: 0, shippingCost: 0, total: 0 };
    }

    const shippingCost = cartItemsLkp.reduce((acc, item) => acc + calculateTotalShipping(item), 0);

    return {
      itemsCount: cartItemsLkp.length,
      shippingCost,
      total: sumMoney(cartItemsLkp.map(calculateCartItemTotal)),
    };
  }, [cartItemsLkp]);

  const enpItemsCount = cartItemsVolGroupedByComposition.length;
  const lkpItemsCount = cartItemsLkp.length;

  const mapGfpDetailsToProductCard = useCallback(
    (gfpDetails: GfpDetails | undefined) => {
      if (isNil(gfpDetails)) {
        return undefined;
      }

      return {
        quality: gfpDetails.quality,
        deliveryDate: gfpDetails.deliveryDate,
        packingQuantity: gfpDetails.packingQuantity,
        quantityPerPacking: gfpDetails.quantityPerPacking,
        series: gfpDetails.series,
        gfpNumber: gfpDetails.gfpNumber,
        lot: gfpDetails.lot,
        qualityObservation1: gfpDetails.qualityObservation1,
        qualityObservation2: gfpDetails.qualityObservation2,
        observation: gfpDetails.observation,
      } as GfpDetails;
    },
    [],
  );

  const mapCartItemToProductCard = useCallback(
    (cartItem: CartItem) => ({
      id: cartItem.id.toString(),
      image: safeGet(cartItem.images?.[0], '/assets/default/image.png'),
      productName: safeGet(cartItem.description, 'Produto sem descrição'),
      producer: safeGet(cartItem.producerName, 'Produtor não informado'),
      quality: safeGet(cartItem.quality, ''),
      packaging: formatPackaging(cartItem),
      deliveryDayLabel: safeGet(cartItem.deliveryDayLabel, ''),
      packagesQuantity: safeGet(cartItem.packingQuantity, 0),
      totalUnits: safeGet(cartItem.unitQuantity, 0),
      packageValue: safeGet(cartItem.packingPrice, 0),
      consolidatedUnitValue: safeGet(cartItem.unitPriceConsolidated, 0),
      unitValue: safeGet(cartItem.unitPrice, 0),
      totalValue: calculateCartItemTotal(cartItem),
      shippingCost: calculateTotalShipping(cartItem),
      unitPerPacking: safeGet(cartItem.unitPerPacking, 0),
      observation: safeGet(cartItem.observation, ''),
      message: safeGet(cartItem.message, ''),
      dimension: safeGet(cartItem.dimension, ''),
      gfpLineId: cartItem.gfpLineId,
      colors: safeGet(cartItem.color, ''),
      productCode: safeGet(cartItem.barcode, ''),
      barCode: safeGet(cartItem.gtincode, ''),
      isExpired: cartItem.isExpired ?? false,
      isExpiredInFront: cartItem.isExpiredInFront ?? false,
      compositionKey: cartItem.compositionKey,
      expirationDate: cartItem.expirationDate,
      gfpDetails: mapGfpDetailsToProductCard(cartItem.gfpDetails),
      offerType: parseOfferType(cartItem.offerType),
      conceptName: cartItem.conceptName,
    }),
    [mapGfpDetailsToProductCard],
  );

  const mapCompositionToProductCard = useCallback((cartItems: CartItem[]) => {
    if (cartItems.length === 0) {
      throw new Error('Cannot map empty composition array');
    }

    const [firstItem] = cartItems;

    const sumProperty = (prop: keyof CartItem) =>
      cartItems.reduce((acc, item) => acc + (Number(item[prop]) || 0), 0);

    const totals = {
      packages: sumProperty('packingQuantity'),
      units: sumProperty('unitQuantity'),
      value: sumMoney(cartItems.map(calculateCartItemTotal)),
      shipping: cartItems.reduce((acc, item) => acc + calculateTotalShipping(item), 0),
    };

    const calculateConsolidatedUnitValue = (): number =>
      totals.units > 0 ? totals.value / totals.units : 0;

    return {
      id: `composition-${firstItem.compositionKey}`,
      image: firstItem.images?.[0] ?? '/assets/default/image.png',
      productName: `${
        firstItem.description ?? 'Produto sem descrição'
      } (Composição)`,
      producer: firstItem.producerName ?? 'Produtor não informado',
      quality: firstItem.quality ?? '',
      packaging: `Composição com ${cartItems.length} itens`,
      deliveryDayLabel: firstItem.deliveryDayLabel ?? '',
      packagesQuantity: totals.packages,
      totalUnits: totals.units,
      packageValue: firstItem.packingPrice ?? 0,
      consolidatedUnitValue: calculateConsolidatedUnitValue(),
      unitValue: firstItem.unitPrice ?? 0,
      totalValue: totals.value,
      shippingCost: totals.shipping,
      unitPerPacking: firstItem.unitPerPacking ?? 0,
      observation: firstItem.observation ?? '',
      message: firstItem.message ?? '',
      dimension: firstItem.dimension ?? '',
      gfpLineId: firstItem.gfpLineId,
      colors: firstItem.color ?? '',
      productCode: firstItem.barcode ?? '',
      barCode: firstItem.gtincode ?? '',
      isComposition: true,
      compositionKey: firstItem.compositionKey,
      compositionItems: cartItems.map((item) => ({
        id: item.id,
        description: item.description ?? 'Item sem descrição',
        quantity: item.unitQuantity ?? 0,
      })),
      offerType: parseOfferType(firstItem.offerType),
      conceptName: firstItem.conceptName,
    };
  }, []);

  const renderProductList = useCallback(
    (items: Array<CartItem[] | CartItem>, keyPrefix: string) =>
      items.map((groupOrItem, index) => {
        if (Array.isArray(groupOrItem)) {
          const productData = mapCompositionToProductCard(groupOrItem);
          return (
            <ProductCartCard
              key={`${keyPrefix}-composition-${groupOrItem[0].compositionKey}-${index}`}
              {...productData}
              onRemove={() => onClickRemoveItem(groupOrItem[0].id)}
            />
          );
        }
        const productData = mapCartItemToProductCard(groupOrItem);
        return (
          <ProductCartCard
            key={`${keyPrefix}-individual-${groupOrItem.id}`}
            {...productData}
            onRemove={() => onClickRemoveItem(groupOrItem.id)}
          />
        );
      }),
    [mapCompositionToProductCard, mapCartItemToProductCard, onClickRemoveItem],
  );

  const renderLkpProducts = useCallback(
    (items: CartItem[], title: string, keyPrefix: string) => (
      <>
        {items.length > 0 && <S.SectionTitle>{title}</S.SectionTitle>}
        {items.map((cartItem) => {
          const productData = mapCartItemToProductCard(cartItem);
          return (
            <ProductCartCard
              key={`${keyPrefix}-${cartItem.id}`}
              {...productData}
              onRemove={() => onClickRemoveItem(cartItem.id)}
            />
          );
        })}
      </>
    ),
    [mapCartItemToProductCard, onClickRemoveItem],
  );

  const expireAllItensVol = useCallback(() => {
    volTimerExpiredRef.current = true;
    setCartItemsVol([]);
    setCartItemsVolGroupedByComposition([]);
  }, []);

  const expireAllItensLkp = useCallback(() => {
    lkpTimerExpiredRef.current = true;
    setCartItemsLkp([]);
    setCartItemsNoGfp([]);
    setCartItemsWithGfp([]);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (volOfferId && volCartExpiration) {
      const expireAt = new Date(volCartExpiration).getTime();
      if (!Number.isNaN(expireAt) && expireAt > 0) {
        const delay = expireAt - Date.now();
        if (delay <= 0) {
          refetchCartItemsVol();
        } else {
          timeoutId = setTimeout(() => refetchCartItemsVol(), delay + 1000);
        }
      }
    }
    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [volCartExpiration, volOfferId, refetchCartItemsVol]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (lkpOfferId && lkpCartExpiration) {
      const expireAt = new Date(lkpCartExpiration).getTime();
      if (!Number.isNaN(expireAt) && expireAt > 0) {
        const delay = expireAt - Date.now();
        if (delay <= 0) {
          refetchCartItemsLkp();
        } else {
          timeoutId = setTimeout(() => refetchCartItemsLkp(), delay + 1000);
        }
      }
    }
    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [lkpCartExpiration, lkpOfferId, refetchCartItemsLkp]);

  const handleClearCart = useCallback(() => {
    const currentTab = activeTab;
    const hasItems =
      currentTab === CartTabType.ENP
        ? cartItemsVol.length > 0
        : cartItemsLkp.length > 0;

    if (!hasItems) {
      return;
    }

    const cartId = currentTab === CartTabType.ENP ? volOfferId : lkpOfferId;
    const cartTypeName = currentTab === CartTabType.ENP ? 'ENP' : 'LKP';

    Modal.confirm({
      title: 'Excluir carrinho',
      icon: <ExclamationCircleOutlined />,
      content: `Tem certeza que deseja excluir todo o carrinho ${cartTypeName}?`,
      okText: 'Sim, excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await getDeleteCart({ cartId }).unwrap();
          dispatch(cartActions.clearSummaryCart());

          if (currentTab === CartTabType.ENP) {
            setCartItemsVol([]);
            setCartItemsVolGroupedByComposition([]);
          } else {
            setCartItemsLkp([]);
            setCartItemsNoGfp([]);
            setCartItemsWithGfp([]);
          }

          console.log(`Carrinho ${cartTypeName} excluído com sucesso`);
        } catch (error) {
          console.error('Erro ao excluir carrinho:', error);
        }
      },
    });
  }, [
    activeTab,
    cartItemsVol.length,
    cartItemsLkp.length,
    volOfferId,
    lkpOfferId,
    getDeleteCart,
  ]);

  const handleFinalizePurchase = useCallback(() => {
    const currentTab = activeTab;

    if (currentTab === CartTabType.ENP) {
      if (cartItemsVol.find((item) => item.isExpired)) {
        setModalInformationsItensExpired(true);
        return;
      }
    }

    if (currentTab === CartTabType.LKP) {
      if (cartItemsLkp.find((item) => item.isExpired)) {
        setModalInformationsItensExpired(true);
        return;
      }
    }

    setModalOrderSummaryIsVisible(true);
  }, [activeTab, cartItemsVol, cartItemsLkp]);

  const handleShowModalSuccess = useCallback(() => {
    setShowModalSuccess(true);
  }, []);

  const closeModalOrderSummary = useCallback(() => {
    setModalOrderSummaryIsVisible(false);
  }, []);

  const onClickMyOrders = useCallback(() => {
    const typeOfferSelected = activeTab === CartTabType.ENP ? 'vol' : 'lkp';
    setTypeOfferSelectedMyOrders(typeOfferSelected);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${producerUrl}/my-orders`);
    setShowModalSuccess(false);
  }, [activeTab, navigate, producerUrl]);

  const renderTimers = useCallback(
    (activeTabType: CartTabType) => {
      const enpTimer =
        activeTabType === CartTabType.ENP ? (
          <S.PrimaryLabel>
            <ClockCircleOutlined />
            Compra <span>ENP</span> será confirmada em:{' '}
            {volOfferId > 0 &&
            !volExpiration.isExpired &&
            !isNaN(volExpiration.expirationDate) ? (
              <S.StyledCountdown>
                <Countdown
                  format="HH:mm:ss"
                  value={volExpiration.expirationDate}
                  onFinish={expireAllItensVol}
                />
              </S.StyledCountdown>
            ) : (
              '00:00:00'
            )}
          </S.PrimaryLabel>
        ) : (
          <S.SecondaryLabel>
            ENP:{' '}
            {volOfferId > 0 &&
            !volExpiration.isExpired &&
            !isNaN(volExpiration.expirationDate) ? (
              <S.StyledCountdown>
                <Countdown
                  format="HH:mm:ss"
                  value={volExpiration.expirationDate}
                  onFinish={expireAllItensVol}
                />
              </S.StyledCountdown>
            ) : (
              '00:00:00'
            )}
          </S.SecondaryLabel>
        );

      const lkpTimer =
        activeTabType === CartTabType.LKP ? (
          <S.LkpActiveLabel>
            <ClockCircleOutlined />
            Compra <b>LKP</b> será confirmada em:{' '}
            {lkpOfferId > 0 &&
            !lkpExpiration.isExpired &&
            !isNaN(lkpExpiration.expirationDate) ? (
              <S.StyledCountdown>
                <Countdown
                  format="HH:mm:ss"
                  value={lkpExpiration.expirationDate}
                  onFinish={expireAllItensLkp}
                />
              </S.StyledCountdown>
            ) : (
              '00:00:00'
            )}
          </S.LkpActiveLabel>
        ) : (
          <S.SecondaryLabel>
            LKP:{' '}
            {lkpOfferId > 0 &&
            !lkpExpiration.isExpired &&
            !isNaN(lkpExpiration.expirationDate) ? (
              <S.StyledCountdown>
                <Countdown
                  format="HH:mm:ss"
                  value={lkpExpiration.expirationDate}
                  onFinish={expireAllItensLkp}
                />
              </S.StyledCountdown>
            ) : (
              '00:00:00'
            )}
          </S.SecondaryLabel>
        );

      return { enpTimer, lkpTimer };
    },
    [
      volOfferId,
      volExpiration,
      lkpOfferId,
      lkpExpiration,
      expireAllItensVol,
      expireAllItensLkp,
    ],
  );

  const onClickCloseShowSuccess = useCallback(() => {
    const isEnpSelected = activeTab === CartTabType.ENP;
    const volItems = volCartItems?.length ? volCartItems : [];
    const lkpItems = lkpCartItems?.length ? lkpCartItems : [];

    const cartItems = isEnpSelected ? lkpItems : volItems;
    if (cartItems?.length) {
      setShowModalSuccess(false);
      setActiveTab(isEnpSelected ? CartTabType.LKP : CartTabType.ENP);
    } else {
      navigate(`/${producerUrl}/products`);
      setShowModalSuccess(false);
    }
  }, [activeTab, volCartItems, lkpCartItems, navigate, producerUrl]);

  const renderEnpSummary = useCallback(
    () => (
      <OrderSummaryCard
        label="Resumo da compra ENP"
        deliveryDate={
          deliveryDate ? moment(deliveryDate).format('DD/MM/YYYY') : ''
        }
        billingDate={
          billingDate ? moment(billingDate).format('DD/MM/YYYY') : ''
        }
        itemsCount={enpTotals.itemsCount}
        shippingCost={enpTotals.shippingCost}
        total={enpTotals.total + enpTotals.shippingCost}
        onClearCart={handleClearCart}
        onFinalizePurchase={handleFinalizePurchase}
      />
    ),
    [
      deliveryDate,
      billingDate,
      enpTotals,
      handleClearCart,
      handleFinalizePurchase,
    ],
  );

  const renderLkpSummary = useCallback(
    () => (
      <OrderSummaryCard
        label="Resumo da compra LKP"
        deliveryDate={
          deliveryDate ? moment(deliveryDate).format('DD/MM/YYYY') : ''
        }
        billingDate={
          billingDate ? moment(billingDate).format('DD/MM/YYYY') : ''
        }
        itemsCount={lkpTotals.itemsCount}
        shippingCost={lkpTotals.shippingCost}
        total={lkpTotals.total + lkpTotals.shippingCost}
        onClearCart={handleClearCart}
        onFinalizePurchase={handleFinalizePurchase}
        isLkp
      />
    ),
    [
      deliveryDate,
      billingDate,
      lkpTotals,
      handleClearCart,
      handleFinalizePurchase,
    ],
  );

  const subHeader = (
    <S.Header className="shopping-cart-sub-header">
      <S.LeftSection>
        <S.BackButton icon={<LeftOutlined />} onClick={navigateToProducts}>
          <S.BackText>Voltar às ofertas</S.BackText>
        </S.BackButton>
        <S.CartText>Seu Carrinho ({enpItemsCount + lkpItemsCount})</S.CartText>
      </S.LeftSection>
    </S.Header>
  );

  const content = (
    <>
      {subHeader}
      <S.Content className="shopping-cart-content">
        <S.TabsContainer>
          <S.TabsHeaderContainer>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as CartTabType)}
              size="large"
            >
              <TabPane
                tab={`Produtos em ENP (${enpItemsCount})`}
                key={CartTabType.ENP}
              >
                <S.TabContent>
                  <S.MobileTimersContainer>
                    {renderTimers(activeTab).enpTimer}
                    {renderTimers(activeTab).lkpTimer}
                  </S.MobileTimersContainer>

                  <S.ResponsiveGrid>
                    <S.ProductsSection>
                      {(volOfferId && isFetchingENP) || isFetchingLKP ? (
                        <TabLoading />
                      ) : cartItemsVolGroupedByComposition.length === 0 ? (
                        <TabEmpty
                          message="Nenhum produto ENP"
                          subMessage="Adicione produtos para visualizar aqui"
                        />
                      ) : (
                        renderProductList(
                          cartItemsVolGroupedByComposition,
                          'enp',
                        )
                      )}
                    </S.ProductsSection>
                    <S.SummarySection>{renderEnpSummary()}</S.SummarySection>
                  </S.ResponsiveGrid>
                </S.TabContent>
              </TabPane>
              <TabPane
                tab={`Produtos em LKP (${lkpItemsCount})`}
                key={CartTabType.LKP}
              >
                <S.TabContent>
                  <S.MobileTimersContainer>
                    {renderTimers(activeTab).enpTimer}
                    {renderTimers(activeTab).lkpTimer}
                  </S.MobileTimersContainer>

                  <S.ResponsiveGrid>
                    <S.ProductsSection>
                      {lkpOfferId && isFetchingLKP ? (
                        <TabLoading />
                      ) : cartItemsNoGfp.length === 0 &&
                        cartItemsWithGfp.length === 0 ? (
                        <TabEmpty
                          message="Nenhum produto LKP"
                          subMessage="Adicione produtos para visualizar aqui"
                        />
                      ) : (
                        <>
                          {renderLkpProducts(
                            cartItemsNoGfp,
                            'Produtos',
                            'lkp-nogfp',
                          )}
                          {renderLkpProducts(
                            cartItemsWithGfp,
                            'Compras por GFP',
                            'lkp-gfp',
                          )}
                        </>
                      )}
                    </S.ProductsSection>
                    <S.SummarySection>{renderLkpSummary()}</S.SummarySection>
                  </S.ResponsiveGrid>
                </S.TabContent>
              </TabPane>
            </Tabs>

            <S.TimersContainer>
              {renderTimers(activeTab).enpTimer}
              {renderTimers(activeTab).lkpTimer}
            </S.TimersContainer>
          </S.TabsHeaderContainer>
        </S.TabsContainer>
      </S.Content>
    </>
  );

  return (
    <>
      <LoggedScreen content={content} showSubHeader={false} />
      <ModalSuccessfulPurchase
        modalIsVisible={showModalSuccess}
        actionButtonOk={onClickMyOrders}
        actionButtonCancel={onClickCloseShowSuccess}
      />
      <ModalOrderSummary
        setModalIsVisible={closeModalOrderSummary}
        modalIsVisible={modalOrderSummaryIsVisible}
        showModalSuccess={handleShowModalSuccess}
        isLkp={activeTab === CartTabType.LKP}
      />
      <ModalInformationExpiredItems
        modalIsVisible={modalInformationsItensExpired}
        setModalIsVisible={setModalInformationsItensExpired}
      />
    </>
  );
}
